/**
 * `PharmaParams` — listy wartosci, schemat Zoda i BRAMKA `dosageDepiction`
 * (docs/VERTICALS.md § PharmaParams).
 *
 * DLACZEGO OSOBNY PLIK: `src/shared/scene/spec.ts` importuje ten schemat, a
 * `pharma.ts` importuje `registry.ts`, ktore importuje WARTOSC `VERTICALS` ze
 * `spec.ts`. Schemat w `pharma.ts` dalby cykl `spec -> pharma -> registry -> spec`
 * padajacy w ESM na TDZ. Ten plik jest LISCIEM: importuje wylacznie `zod`.
 * `pharma.ts` re-eksportuje calosc.
 *
 * BRAMKA: cokolwiek poza `dosageDepiction: 'none'` wymaga niepustego
 * `regulatoryReviewedBy`. Walidacja siedzi TUTAJ, czyli w `src/shared/` — ta sama
 * sciezka waliduje UI, kompilator i request do API
 * (`SceneSpecSchema` -> `VerticalParamsSchema` -> `PharmaParamsSchema`). NIE DA
 * SIE tego obejsc wlasnym requestem: zapis rewizji odrzuca spec na walidacji
 * (422), a kompilator odmawia kompilacji.
 */

import { z } from 'zod';

// ─────────────────────────────────────────────────────────────────────────────
// Listy wartosci (kolejnosc DOKLADNIE jak w docs/VERTICALS.md)
// ─────────────────────────────────────────────────────────────────────────────

export const PHARMA_LABEL_POLICIES = ['locked_exact', 'locked_partial_crop', 'blurred_generic'] as const;
export type PharmaLabelPolicy = (typeof PHARMA_LABEL_POLICIES)[number];

export const PHARMA_LABEL_LEGIBILITIES = ['must_be_fully_legible', 'primary_panel_legible', 'not_required'] as const;
export type PharmaLabelLegibility = (typeof PHARMA_LABEL_LEGIBILITIES)[number];

/**
 * TODO(spec): VERTICALS.md wymienia tylko `keep_blank` (domyslne). Dodaje
 * `as_in_reference` dla przypadku, gdy referencja ma prawdziwy, zatwierdzony kod
 * serii do odwzorowania. Czy taka wartosc jest dopuszczalna?
 */
export const PHARMA_BATCH_CODE_AREAS = ['keep_blank', 'as_in_reference'] as const;
export type PharmaBatchCodeArea = (typeof PHARMA_BATCH_CODE_AREAS)[number];

export const PHARMA_CERTIFICATION_BADGES = ['none', 'reference_only'] as const;
export type PharmaCertificationBadges = (typeof PHARMA_CERTIFICATION_BADGES)[number];

export const PHARMA_DOSAGE_DEPICTIONS = ['none', 'single_unit', 'measured_scoop', 'in_palm', 'scattered'] as const;
export type PharmaDosageDepiction = (typeof PHARMA_DOSAGE_DEPICTIONS)[number];

export const PHARMA_RX_OTC_CLASSES = ['otc', 'rx', 'supplement', 'cosmetic_derma', 'medical_device'] as const;
export type PharmaRxOtcClass = (typeof PHARMA_RX_OTC_CLASSES)[number];

export const PHARMA_SOFTBOX_SIZES = ['small', 'medium', 'large', 'wall'] as const;
export type PharmaSoftboxSize = (typeof PHARMA_SOFTBOX_SIZES)[number];

export const PHARMA_STRIP_SPECULARS = ['none', 'single_broad', 'dual_edge', 'wrap'] as const;
export type PharmaStripSpecular = (typeof PHARMA_STRIP_SPECULARS)[number];

export const PHARMA_FOIL_SHEENS = ['brushed', 'satin', 'mirror'] as const;
export type PharmaFoilSheen = (typeof PHARMA_FOIL_SHEENS)[number];

export const PHARMA_EDGE_DEFINITIONS = ['soft', 'crisp'] as const;
export type PharmaEdgeDefinition = (typeof PHARMA_EDGE_DEFINITIONS)[number];

export const PHARMA_DROPPER_POSITIONS = ['in', 'beside', 'dripping'] as const;
export type PharmaDropperPosition = (typeof PHARMA_DROPPER_POSITIONS)[number];

export const PHARMA_CARTON_PANELS = ['front', 'front_side', 'front_side_top'] as const;
export type PharmaCartonPanels = (typeof PHARMA_CARTON_PANELS)[number];

export const PHARMA_TRANSPARENCY_HANDLINGS = ['opaque', 'translucent', 'clear_with_contents'] as const;
export type PharmaTransparencyHandling = (typeof PHARMA_TRANSPARENCY_HANDLINGS)[number];

export const PHARMA_STYLING_WORLDS = [
  'clinical_white',
  'pharmacy_shelf',
  'laboratory_clean',
  'wellness_natural',
  'apothecary_botanical',
  'spa_calm',
  'sport_performance',
  'luxury_derma',
  'kitchen_daily_ritual',
] as const;
export type PharmaStylingWorld = (typeof PHARMA_STYLING_WORLDS)[number];

/** Swiaty czytane jako kontekst lifestyle — dokumentuja konflikt z `rxOtcClass: 'rx'`. */
export const PHARMA_LIFESTYLE_WORLDS: readonly PharmaStylingWorld[] = [
  'wellness_natural',
  'spa_calm',
  'sport_performance',
  'kitchen_daily_ritual',
];

export const PHARMA_SURFACE_PRESETS = [
  'seamless_white_sweep',
  'matte_acrylic',
  'frosted_glass',
  'travertine',
  'marble',
  'brushed_steel',
  'linen',
  'slate',
  'water_surface',
  'bamboo',
  'pill_organizer_tray',
] as const;
export type PharmaSurfacePreset = (typeof PHARMA_SURFACE_PRESETS)[number];

export const PHARMA_INGREDIENT_PROPS = [
  'botanical_sprig',
  'citrus_slice',
  'turmeric_root',
  'collagen_scoop',
  'omega_capsule',
  'water_droplets',
  'ice',
  'honey_drizzle',
  'oat_grains',
  'fresh_berries',
  'mortar_pestle',
  'measuring_cup',
  'glass_of_water',
] as const;
export type PharmaIngredientProp = (typeof PHARMA_INGREDIENT_PROPS)[number];

/**
 * Propsy bramkowane przez `claimGuardrails` (docs/VERTICALS.md: "czesc
 * bramkowana przez `claimGuardrails`"). Sugeruja dawkowanie albo przygotowanie
 * preparatu, wiec przy wlaczonych guardrailach wypadaja z promptu i trafiaja do
 * `DO_NOT`.
 */
export const PHARMA_CLAIM_GATED_PROPS: readonly PharmaIngredientProp[] = [
  'collagen_scoop',
  'omega_capsule',
  'mortar_pestle',
  'measuring_cup',
];

export const PHARMA_MULTI_PACKS = [
  'single',
  'duo',
  'trio_stepped',
  'family_lineup',
  'box_plus_blister',
  'box_plus_bottle',
] as const;
export type PharmaMultiPack = (typeof PHARMA_MULTI_PACKS)[number];

export const PHARMA_HYGIENE_CUES = ['sterile_pristine', 'in_use_open', 'sealed_cellophane'] as const;
export type PharmaHygieneCue = (typeof PHARMA_HYGIENE_CUES)[number];

export const PHARMA_SHELF_CONTEXTS = [
  'isolated',
  'single_shelf',
  'pharmacy_shelf_bokeh',
  'bathroom_counter',
  'gym_bag',
] as const;
export type PharmaShelfContext = (typeof PHARMA_SHELF_CONTEXTS)[number];

// ─────────────────────────────────────────────────────────────────────────────
// Podschematy obiektowe
// ─────────────────────────────────────────────────────────────────────────────

export const PharmaGlareControlSchema = z.strictObject({
  softboxSize: z.enum(PHARMA_SOFTBOX_SIZES).optional(),
  stripSpecular: z.enum(PHARMA_STRIP_SPECULARS).optional(),
  capReflectionSuppression: z.boolean().optional(),
  /** TWARDE. Domysl to `true` — brak pola znaczy "zakaz obowiazuje". */
  labelHotspotForbidden: z.boolean().optional(),
});
export type PharmaGlareControl = z.infer<typeof PharmaGlareControlSchema>;

export const PharmaBlisterOpticsSchema = z.strictObject({
  foilSheen: z.enum(PHARMA_FOIL_SHEENS).optional(),
  cavityCount: z.int().min(1).max(60).optional(),
  cavityFillVisible: z.boolean().optional(),
  avoidMoire: z.boolean().optional(),
  foilAngleDeg: z.int().min(-180).max(180).optional(),
});
export type PharmaBlisterOptics = z.infer<typeof PharmaBlisterOpticsSchema>;

export const PharmaGlassHandlingSchema = z.strictObject({
  edgeDefinition: z.enum(PHARMA_EDGE_DEFINITIONS).optional(),
  refractionStrength: z.number().min(0).max(1).optional(),
  meniscusVisible: z.boolean().optional(),
  liquidLevelPct: z.number().min(0).max(100).optional(),
  liquidColorHex: z
    .string()
    .regex(/^#[0-9A-Fa-f]{6}$/, 'expected #RRGGBB color')
    .optional(),
  dropperPosition: z.enum(PHARMA_DROPPER_POSITIONS).optional(),
});
export type PharmaGlassHandling = z.infer<typeof PharmaGlassHandlingSchema>;

export const PharmaPlasticHandlingSchema = z.strictObject({
  hdpeOpacity: z.number().min(0).max(1).optional(),
  tamperSealVisible: z.boolean().optional(),
  ribbedCapDetail: z.boolean().optional(),
});
export type PharmaPlasticHandling = z.infer<typeof PharmaPlasticHandlingSchema>;

export const PharmaCartonOpticsSchema = z.strictObject({
  edgeCrispness: z.number().min(0).max(1).optional(),
  embossVisible: z.boolean().optional(),
  spotUvGloss: z.boolean().optional(),
  foilStampSheen: z.number().min(0).max(1).optional(),
  panelsVisible: z.enum(PHARMA_CARTON_PANELS).optional(),
});
export type PharmaCartonOptics = z.infer<typeof PharmaCartonOpticsSchema>;

/**
 * TODO(spec): VERTICALS.md opisuje `audienceRestrictions` proza ("bez dzieci,
 * bez sugestii ciazy, bez grup wrazliwych bez jawnej zgody") i nie podaje typu.
 * Modeluje to jako trzy przelaczniki DOMYSLNIE WLACZONE (brak pola = zakaz
 * obowiazuje) plus referencja do zgody, ktora jako jedyna pozwala je zdjac.
 */
export const PharmaAudienceRestrictionsSchema = z.strictObject({
  noMinors: z.boolean().optional(),
  noPregnancyCues: z.boolean().optional(),
  noVulnerableGroups: z.boolean().optional(),
  explicitConsentRefId: z.string().min(1).max(200).optional(),
});
export type PharmaAudienceRestrictions = z.infer<typeof PharmaAudienceRestrictionsSchema>;

/**
 * TODO(spec): `languageLock` jest w dokumentacji opisany zdaniem ("jezyk
 * etykiety musi odpowiadac rynkowi referencji") bez typu. Modeluje to jako
 * przelacznik (domyslnie wlaczony) z opcjonalnym rynkiem docelowym.
 */
export const PharmaLanguageLockSchema = z.strictObject({
  enabled: z.boolean().optional(),
  marketLocale: z.string().min(2).max(35).optional(),
});
export type PharmaLanguageLock = z.infer<typeof PharmaLanguageLockSchema>;

// ─────────────────────────────────────────────────────────────────────────────
// Schemat + BRAMKA
// ─────────────────────────────────────────────────────────────────────────────

/** Kod bledu bramki — route mapuje go na 422. */
export const PHARMA_DOSAGE_GATE_CODE = 'dosage_depiction_requires_regulatory_review';

/**
 * Komunikat bramki. Trafia do `issues[].message` w `SceneSpecValidationError`,
 * wiec mowi CO zrobic, nie tylko ze jest zle.
 */
export const PHARMA_DOSAGE_GATE_MESSAGE =
  'dosageDepiction other than "none" requires a non-empty regulatoryReviewedBy (regulatory sign-off)';

/** Czy przedstawienie dawki wymaga podpisu regulacyjnego. */
export function dosageDepictionNeedsReview(dosageDepiction: PharmaDosageDepiction | undefined): boolean {
  return dosageDepiction !== undefined && dosageDepiction !== 'none';
}

const PharmaParamsShapeSchema = z.strictObject({
  vertical: z.literal('pharma'),

  // Regulacje i wiernosc
  labelPolicy: z.enum(PHARMA_LABEL_POLICIES).optional(),
  labelLegibility: z.enum(PHARMA_LABEL_LEGIBILITIES).optional(),
  claimGuardrails: z.boolean().optional(),
  batchCodeArea: z.enum(PHARMA_BATCH_CODE_AREAS).optional(),
  certificationBadges: z.enum(PHARMA_CERTIFICATION_BADGES).optional(),
  dosageDepiction: z.enum(PHARMA_DOSAGE_DEPICTIONS).optional(),
  /**
   * Osoba lub zespol, ktory zatwierdzil przedstawienie dawki. NIE jest
   * parametrem promptu — nazwisko nigdy nie trafia do modelu — ale jest
   * warunkiem bramki `dosageDepiction`.
   */
  regulatoryReviewedBy: z.string().min(1).max(200).optional(),
  audienceRestrictions: PharmaAudienceRestrictionsSchema.optional(),
  languageLock: PharmaLanguageLockSchema.optional(),
  rxOtcClass: z.enum(PHARMA_RX_OTC_CLASSES).optional(),

  // Optyka i odblaski
  glareControl: PharmaGlareControlSchema.optional(),
  blisterOptics: PharmaBlisterOpticsSchema.optional(),
  glassHandling: PharmaGlassHandlingSchema.optional(),
  plasticHandling: PharmaPlasticHandlingSchema.optional(),
  cartonOptics: PharmaCartonOpticsSchema.optional(),
  transparencyHandling: z.enum(PHARMA_TRANSPARENCY_HANDLINGS).optional(),

  // Styling
  stylingWorld: z.enum(PHARMA_STYLING_WORLDS).optional(),
  surfacePreset: z.enum(PHARMA_SURFACE_PRESETS).optional(),
  ingredientProps: z.array(z.enum(PHARMA_INGREDIENT_PROPS)).max(PHARMA_INGREDIENT_PROPS.length).optional(),
  multiPack: z.enum(PHARMA_MULTI_PACKS).optional(),
  hygieneCue: z.enum(PHARMA_HYGIENE_CUES).optional(),
  shelfContext: z.enum(PHARMA_SHELF_CONTEXTS).optional(),
  trustSignal: z.number().min(0).max(1).optional(),
});

/**
 * `PharmaParams`. Wszystkie pola poza dyskryminatorem sa OPCJONALNE (pusty
 * `{ vertical: 'pharma' }` musi byc walidny), a semantyke "domyslnie wlaczone"
 * niosa deskryptory: brak `claimGuardrails` znaczy WLACZONE, brak
 * `batchCodeArea` znaczy `keep_blank`, brak `labelPolicy` znaczy `locked_exact`.
 *
 * `.refine` na obiekcie: w Zod 4 refinement zostaje WEWNATRZ schematu, wiec
 * `def.type` to nadal `'object'` — `z.discriminatedUnion('vertical', ...)` w
 * `spec.ts` i walker sciezek w `patch.ts` dzialaja bez zmian.
 */
export const PharmaParamsSchema = PharmaParamsShapeSchema.refine(
  (params) =>
    !dosageDepictionNeedsReview(params.dosageDepiction) ||
    (params.regulatoryReviewedBy !== undefined && params.regulatoryReviewedBy.trim().length > 0),
  {
    error: PHARMA_DOSAGE_GATE_MESSAGE,
    path: ['regulatoryReviewedBy'],
  },
);
export type PharmaParams = z.infer<typeof PharmaParamsSchema>;

/** Jawne domysly pakietu — wszystkie guardraile wlaczone. */
export function defaultPharmaParams(): PharmaParams {
  return {
    vertical: 'pharma',
    labelPolicy: 'locked_exact',
    labelLegibility: 'must_be_fully_legible',
    claimGuardrails: true,
    batchCodeArea: 'keep_blank',
    certificationBadges: 'none',
    dosageDepiction: 'none',
    languageLock: { enabled: true },
    rxOtcClass: 'otc',
    glareControl: { softboxSize: 'large', labelHotspotForbidden: true },
    transparencyHandling: 'opaque',
    stylingWorld: 'clinical_white',
    surfacePreset: 'seamless_white_sweep',
    multiPack: 'single',
    hygieneCue: 'sterile_pristine',
    shelfContext: 'isolated',
    trustSignal: 0.25,
  };
}
